/**
 * Shared constants.
 *
 * Status→colour mappings do NOT live here. They live in `./semantics.ts`, the
 * single derivation, and the conformance gate fails the build if one reappears
 * anywhere else. The four configs below are thin views onto it, kept because
 * many call sites already import them by name.
 */

import { status, provenance, statusKeys } from "./semantics.ts";

export { CONFIDENCE } from "./semantics.ts";

/**
 * Reliance-age policy (S4, 2026-07-12): regulatory data older than this is
 * flagged as possibly stale in the project header. The dataset's applied_at
 * is the clock — law changes whether or not we re-sync, so silence past this
 * age would overstate freshness.
 */
export const MAX_RELIANCE_AGE_DAYS = 90;

/**
 * Country is a category, not a judgement. Categories are carried by the flag
 * and the label — never by a status hue, which would imply something about the
 * country. Previously KE/NG/ZA borrowed the brand/warning/info status colours
 * (so Nigeria rendered permanently amber, the colour of "needs attention")
 * while NA/ZM/GH used raw Tailwind palette classes that did not survive dark
 * mode at all.
 */
const BADGE = { badgeBg: "bg-surface-tertiary", badgeText: "text-text-primary" } as const;

export const COUNTRY_META = {
  KE: { name: "Kenya",        flag: "🇰🇪", currency: "KES", currencySymbol: "KSh",  ...BADGE },
  NG: { name: "Nigeria",      flag: "🇳🇬", currency: "NGN", currencySymbol: "₦",    ...BADGE },
  ZA: { name: "South Africa", flag: "🇿🇦", currency: "ZAR", currencySymbol: "R",    ...BADGE },
  NA: { name: "Namibia",      flag: "🇳🇦", currency: "NAD", currencySymbol: "N$",   ...BADGE },
  ZM: { name: "Zambia",       flag: "🇿🇲", currency: "ZMW", currencySymbol: "K",    ...BADGE },
  GH: { name: "Ghana",        flag: "🇬🇭", currency: "GHS", currencySymbol: "GH₵",  ...BADGE },
} as const;

export const SUPPORTED_COUNTRIES = Object.keys(COUNTRY_META) as Array<keyof typeof COUNTRY_META>;

/** Feature flag: the graded "Investor Readiness / Bankability" surface is a fields-filled % that reads
 *  as a value score — misleading to lenders. Hidden per founder decision (#16); flip to re-enable only
 *  with a real value-based score (DSCR, tariff-in-benchmark, DC/AC, tenor). `computeBankability` still
 *  runs (API/tests unchanged) — this only gates the UI/report surfaces. */
export const SHOW_BANKABILITY = false;

/**
 * The one nudge filter for every surface (hero, tab badge, evaluation panel).
 * While bankability is hidden (#16), its nudge must be hidden EVERYWHERE —
 * three surfaces filtering independently is how the badge came to count a
 * nudge the hero refused to show.
 */
export function visibleNudges<T extends { type: string }>(nudges: T[] | null | undefined): T[] {
  const all = nudges ?? [];
  return SHOW_BANKABILITY ? all : all.filter((n) => n.type !== "bankability");
}

/** Check if a string is a supported country code. */
export function isSupportedCountry(code: string): code is keyof typeof COUNTRY_META {
  return code in COUNTRY_META;
}

/**
 * Build a `{ key: Style }` view of one status kind, for legacy call sites.
 * The key tuples are declared here so `keyof typeof LICENSE_STATUS_CONFIG`
 * stays a literal union — Object.fromEntries alone would widen every key to
 * `string` and silently un-typecheck the `status:` props of seven components.
 * A test asserts each tuple matches statusKeys() so the two cannot drift.
 */
const view = <const K extends readonly string[]>(kind: Parameters<typeof status>[0], keys: K) =>
  Object.fromEntries(keys.map((k) => [k, status(kind, k)])) as Record<K[number], ReturnType<typeof status>>;

export const LICENSE_STATUS_KEYS = ["not_started", "blocked", "preparing", "additional_info_requested", "submitted", "under_review", "approved", "rejected", "expired", "unrecognized"] as const;
export const COMPLIANCE_STATUS_KEYS = ["not_assessed", "compliant", "in_progress", "non_compliant", "exempt", "not_applicable"] as const;
export const GOVERNMENT_TIER_KEYS = ["gate", "pre_operational", "ongoing"] as const;

export const LICENSE_STATUS_CONFIG = view("license", LICENSE_STATUS_KEYS);
export const COMPLIANCE_STATUS_CONFIG = view("compliance", COMPLIANCE_STATUS_KEYS);
export const GOVERNMENT_TIER_CONFIG = view("tier", GOVERNMENT_TIER_KEYS);

export const PROVENANCE_CONFIG = {
  manual: provenance("manual"),
  extracted: provenance("extracted"),
  calculated: provenance("calculated"),
  needs_review: provenance("needs_review"),
  empty: provenance("empty"),
} as const;

export const FIELD_CATEGORIES = [
  { id: "company",       label: "Company" },
  { id: "site",          label: "Site" },
  { id: "plant",         label: "Plant Technical" },
  { id: "financial",     label: "Financial" },
  { id: "environmental", label: "Environmental" },
  { id: "grid",          label: "Grid Connection" },
  { id: "offtake",       label: "Offtake" },
  { id: "community",     label: "Community" },
  { id: "import",        label: "Import & Equipment" },
  { id: "timeline",      label: "Timeline" },
  { id: "regulatory",    label: "Regulatory" },
] as const;

/* CONFIDENCE now lives in ./semantics.ts, beside confidenceTier() and the
   always-confirm field list it governs, and is re-exported at the top of this
   file. Thresholds and the rules that read them belong together. */

/** Capacity routing thresholds in kW (from REGULATORY_SEED_DATA.md §4.3) */
export const CAPACITY_THRESHOLDS = {
  KE_NET_METERING_MAX: 1000,   // ≤1MW: net metering / exempt
  NG_REGISTRATION_MAX: 100,    // ≤100kW: registration only
  NG_MINI_GRID_MAX: 1000,      // ≤1MW: mini-grid permit
  ZA_SSEG_MAX: 100,            // ≤100kW: municipal SSEG registration
  NA_NET_METERING_MAX: 500,    // ≤500kW: ECB net metering
  ZM_LICENSE_EXEMPT_MAX: 250,  // <250kW own-use: no ERB license required (Electricity Act 2019)
  GH_EMBEDDED_MAX: 500,        // ≤500kW: simplified permit (Energy Commission)
} as const;
